import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Cards, ContentLayout, Header,
    LineChart, Spinner, SpaceBetween, StatusIndicator,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { alphaAnalysisApi } from '../../client/clients';
import { SectorDetailResponse, AlphaResult } from '../../api';
import { useArticlePopup, InlineSpinner } from './ArticlePopupModal';
import { GetArticleDetailsCredentialKeyEnum } from '../../api';
import { PublisherMetricsMiniChart } from './PublisherMetricsMiniChart';
import { PublisherOverallBadge } from './PublisherOverallBadge';
import { usePublisherMetrics } from './usePublisherMetrics';
import { buildSectorOutlookSeries, computeSectorForecast } from './sectorForecastUtils';
import { SectorForecastStrip } from './SectorForecastStrip';

// ─── helpers ─────────────────────────────────────────────────────────────────

function stanceColor(s: number): 'success' | 'warning' | 'error' {
    return s >= 20 ? 'success' : s >= -20 ? 'warning' : 'error';
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AlphaSectorDetailPage: React.FC = () => {
    const { sector: sectorParam } = useParams<{ sector: string }>();
    const sector = sectorParam?.replace(/--/g, ' / ');
    const navigate = useNavigate();
    useDocumentTitle(`Alpha — ${sector}`);

    const [data, setData] = useState<SectorDetailResponse | null>(null);
    const [days, setDays] = useState(365);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { openArticle, loadingArticleId, modalElement } = useArticlePopup();

    useEffect(() => {
        setLoading(true);
        setError(null);
        alphaAnalysisApi.getSectorDetail(sector!, days)
            .then(r => setData(r.data))
            .catch(e => { console.error(e); setError('Failed to load sector data'); })
            .finally(() => setLoading(false));
    }, [sector, days]);

    const articles = useMemo(() => data?.articles ?? [], [data]);

    const authorIds = useMemo(
        () => articles.map(a => a.authorId).filter((id): id is string => !!id),
        [articles]);
    const { metricsByAuthor } = usePublisherMetrics(authorIds);

    // Single aggregate sector outlook line — replaces the old top-N-tickers
    // multi-line chart that was almost always empty because most sectors don't
    // have 10 articles per ticker × day. Daily resolution. The series title is
    // intentionally short ("Daily") so it doesn't visually duplicate the
    // yTitle below the chart in the Cloudscape legend.
    const outlookSeries = useMemo(() => {
        const points = buildSectorOutlookSeries(articles);
        return [{
            title: 'Daily',
            type: 'line' as const,
            data: points.map(p => ({ x: new Date(p.date), y: p.value })),
        }];
    }, [articles]);

    // Publisher-weighted forward forecast — same shape as the per-stock page
    // forecast, but at the sector level (every embedded ticker contributes).
    const sectorForecast = useMemo(
        () => computeSectorForecast(articles, metricsByAuthor),
        [articles, metricsByAuthor],
    );

    const xStart = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const xEnd   = new Date();

    if (loading) return <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>;
    if (error)   return <Box padding="l"><StatusIndicator type="error">{error}</StatusIndicator></Box>;
    if (!data)   return null;

    return (
        <ContentLayout
            header={
                <Header
                    variant="h1"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            {[30, 90, 180, 365].map(d => (
                                <Button
                                    key={d}
                                    variant={days === d ? 'primary' : 'normal'}
                                    onClick={() => setDays(d)}
                                >
                                    {d}d
                                </Button>
                            ))}
                        </SpaceBetween>
                    }
                >
                    {sector} — Sector Analysis
                </Header>
            }
        >
            <SpaceBetween size="l">
                <LineChart
                    series={outlookSeries}
                    xDomain={[xStart, xEnd]}
                    yDomain={[-100, 100]}
                    xTitle="Date"
                    yTitle="Conviction-weighted outlook"
                    height={300}
                    empty={<Box textAlign="center">No articles for this sector in the selected window</Box>}
                    noMatch={<Box textAlign="center">No matching data</Box>}
                />

                {/* Publisher-weighted forward forecast — 10 horizons from 1h to 6m.
                    Each cell shows the conviction- and publisher-track-record-weighted
                    expected % return for a basket of every ticker in this sector at
                    that horizon. Mirrors the per-stock chart's forecast line, but as
                    a horizontal strip since sectors don't have a single price. */}
                <div style={{ background: '#fff', border: '1px solid #e9ebed', borderRadius: 8, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                            Forward forecast
                        </span>
                        <span style={{ fontSize: 12, color: '#5f6b7a' }}>
                            publisher-weighted, 10 horizons
                        </span>
                    </div>
                    <SectorForecastStrip forecast={sectorForecast} />
                </div>

                <Cards
                    items={articles}
                    cardDefinition={{
                        header: (item: AlphaResult) => {
                            const artId    = item.articleId ?? '';
                            const isLoading = loadingArticleId === artId;
                            return (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <button
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0972d3', textAlign: 'left', fontSize: 'inherit', fontWeight: 'inherit' }}
                                        onClick={() => artId && openArticle(artId, GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie)}
                                        disabled={isLoading || !artId}
                                    >
                                        {item.articleTitle ?? ''}
                                    </button>
                                    <InlineSpinner show={isLoading} />
                                </span>
                            );
                        },
                        sections: [
                            {
                                id: 'date',
                                header: 'Published',
                                content: (item: AlphaResult) => (item.publishedAt ?? '').substring(0, 10),
                            },
                            {
                                id: 'author',
                                header: 'Author',
                                content: (item: AlphaResult) => (
                                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                        <span
                                            style={{ cursor: 'pointer', color: '#0972d3' }}
                                            onClick={() => navigate(`/alpha/analysts/${item.authorId}`)}
                                        >
                                            {item.authorName}
                                        </span>
                                        {item.authorId && (
                                            <>
                                                <PublisherOverallBadge metrics={metricsByAuthor[item.authorId]} />
                                                <PublisherMetricsMiniChart
                                                    metrics={metricsByAuthor[item.authorId]}
                                                    authorName={item.authorName}
                                                />
                                            </>
                                        )}
                                    </SpaceBetween>
                                ),
                            },
                            {
                                id: 'outlook',
                                header: 'Sector Outlook',
                                content: (item: AlphaResult) => (
                                    <StatusIndicator type={stanceColor(item.sectorOutlook ?? 0)}>
                                        {(item.sectorOutlook ?? 0) > 0 ? '+' : ''}{item.sectorOutlook ?? 0}
                                    </StatusIndicator>
                                ),
                            },
                            {
                                id: 'tickers',
                                header: 'Tickers',
                                content: (item: AlphaResult) => (
                                    <SpaceBetween direction="horizontal" size="xs">
                                        {(item.tickers ?? []).map(t => (
                                            <Button key={t.ticker} variant="link"
                                                onClick={() => navigate(`/alpha/stocks/${t.ticker}`)}>
                                                {t.ticker} ({(t.stance ?? 0) > 0 ? '+' : ''}{t.stance ?? 0})
                                            </Button>
                                        ))}
                                    </SpaceBetween>
                                ),
                            },
                        ],
                    }}
                    trackBy="articleId"
                    empty={<Box textAlign="center">No articles yet</Box>}
                />
                {modalElement}
            </SpaceBetween>
        </ContentLayout>
    );
};
